import { create, getAll, remove } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';
import { getSessionItem, setSessionItem, STORAGE_KEYS } from '@/utils/infrastructure/auth';
import { filterAutomaticWelcomeMessages } from '@/utils/infrastructure/communication';

// Small helper to wait between retries (to mitigate eventual consistency on Azure Hyperscale)
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const { ChatControlMode, ChatHandlerType } = CoreEnums;

const isoToMs = (iso) => {
  if (!iso) return 0;
  const s = String(iso);
  return (s.includes('Z') || s.includes('+') ? new Date(s) : new Date(`${s}Z`)).getTime();
};

// Returns the timestamp to use when ORDERING messages chronologically.
//
// The backend sometimes emits a message (notably `response`) with a
// `created` that pre-dates the actual emission time — e.g. it reuses the
// turn-start timestamp. If we sorted by that value the response would
// land BEFORE the ai-thinking timeline of the same turn, even though the
// user clearly receives it AFTER.
//
// We tag socket-delivered messages with `socketReceivedAt = Date.now()`
// at receive time and prefer that over `createdMs` for ordering. The
// original `created` ISO string and `createdMs` are preserved untouched
// so the UI can still display the backend timestamp ("15:34") in the
// message footer.
const getOrderTs = (msg) => {
  if (!msg) return 0;
  if (typeof msg.socketReceivedAt === 'number' && msg.socketReceivedAt > 0) {
    return msg.socketReceivedAt;
  }
  if (typeof msg.createdMs === 'number' && msg.createdMs > 0) {
    return msg.createdMs;
  }
  return isoToMs(msg.created);
};

const initialState = {
  chatThread: {},
  messages: [],
  threads: [],
  tasks: [],
  taskRuns: [],
  emailTracking: [],
  emailTrackingCount: 0,
  totalCount: 0, // For messages
  totalThreads: 0, // For threads (separate field)
  tasksCount: 0,
  taskRunsCount: 0,
  page: 1,
  pageSize: 10,
  isOpen: false,
  loadingMore: false,
  messageBuffer: {}, // Buffer for messages by threadId
  // True while an agent switch is in flight (between clearMessages and the new
  // thread's messages loading). Forces late socket messages to be buffered
  // instead of injected into the freshly-cleared list (avoids stale flashes).
  agentSwitching: false,
  // Multi-agent support
  agents: [],
  selectedAgent: null,
  loadingAgents: false,
  agentsPage: 1,
  agentsPageSize: 5,
  totalAgents: 0,
  loadingMoreAgents: false,
  // Source of truth for "load more": derived from the actual items returned by
  // the API (a short/empty page means the end), NOT just `agents.length <
  // totalAgents`, because the server `$count` can exceed the number of agents
  // the paginated query actually returns (permission/active filters, etc.).
  hasMoreAgents: false,
  // Agent search (server-side). Kept in a SEPARATE slice so the global
  // `agents` list stays intact for id lookups (auto-select, timeline icons,
  // changeAgent). The dropdown renders these results while a term is active.
  agentSearchTerm: '',
  agentSearchResults: [],
  agentSearchPage: 1,
  totalAgentSearch: 0,
  hasMoreAgentSearch: false,
  loadingAgentSearch: false,
  loadingMoreAgentSearch: false,
  messagesThreadId: null,
  // Thread participants
  threadParticipants: {},
  participantsTotalCount: {},
  loadingParticipants: false,
  hasAllowedAgents: null,
  openSpecificChatTs: 0,
};

const getMessageUniqueKey = (message) => {
  if (!message) return null;
  if (message.messageId) return String(message.messageId).toLowerCase();
  if (message.id) return String(message.id).toLowerCase();
  if (message.channelMessageIdentifier)
    return String(message.channelMessageIdentifier).toLowerCase();
  if (message.content?.messageId) return String(message.content.messageId).toLowerCase();
  if (message.content?.id) return String(message.content.id).toLowerCase();
  // Stable fallback: derive key from created timestamp + userId + messageType
  // to avoid collisions from index-based keys like __idx_N
  if (message.created) {
    const parts = [message.created, message.userId || '', message.messageType || ''].join('|');
    return `derived_${parts}`;
  }
  return null;
};

const pickLatestIsoTimestamp = (first, second) => {
  const normalizeIso = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'string') {
      if (value.includes('Z') || value.includes('+')) return value;
      return `${value}Z`;
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    return null;
  };

  const getMs = (value) => {
    if (!value) return 0;
    const normalized = normalizeIso(value);
    if (!normalized) return 0;
    const parsed = Date.parse(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const firstIso = normalizeIso(first);
  const secondIso = normalizeIso(second);

  const firstMs = getMs(firstIso);
  const secondMs = getMs(secondIso);

  return firstMs >= secondMs ? firstIso || secondIso : secondIso || firstIso;
};

const normalizeIsoForThread = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') {
    if (value.includes('Z') || value.includes('+')) return value;
    return `${value}Z`;
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return null;
};

const getTimestampMs = (value) => {
  if (!value) return 0;
  const normalized = normalizeIsoForThread(value);
  if (!normalized) return 0;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const withThreadTimestamps = (thread, fallbackUpdated) => {
  if (!thread) return thread;
  const candidate = pickLatestIsoTimestamp(thread?.updated || thread?.created, fallbackUpdated);
  const updated =
    candidate ||
    normalizeIsoForThread(thread?.updated) ||
    normalizeIsoForThread(thread?.created) ||
    fallbackUpdated ||
    null;
  const updatedMs = getTimestampMs(
    updated || thread?.updated || thread?.created || fallbackUpdated,
  );

  return {
    ...thread,
    updated,
    updatedMs,
  };
};

const ChatModel = {
  namespace: 'chat',
  state: initialState,
  effects: {
    *resetChatState({ payload }, { put }) {
      try {
        yield put({ type: 'resetState' });
        if (payload?.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchAgents({ payload }, { call, put, select }) {
      try {
        const isLoadingMore = payload?.loadMore;
        const { agentsPage, agentsPageSize } = yield select((state) => state.chat);

        if (isLoadingMore) {
          yield put({ type: 'setLoadingMoreAgents', payload: true });
        } else {
          yield put({ type: 'setLoadingAgents', payload: true });
        }

        const page = isLoadingMore ? agentsPage + 1 : 1;
        const skip = (page - 1) * agentsPageSize;

        const route = `ai/agent/allowed?$count=true&$skip=${skip}&$top=${agentsPageSize}&$select=id,name,isActive,description,iconUrl,provider`;
        const res = yield call(getAll, { route });

        if (isLoadingMore) {
          yield put({ type: 'appendAgents', payload: { ...res, page, pageSize: agentsPageSize } });
          yield put({ type: 'setLoadingMoreAgents', payload: false });
        } else {
          yield put({ type: 'saveAgents', payload: { ...res, page, pageSize: agentsPageSize } });
          yield put({ type: 'setLoadingAgents', payload: false });
        }

        if (payload?.onSuccess) payload.onSuccess(res);
      } catch (e) {
        yield put({ type: 'setLoadingAgents', payload: false });
        yield put({ type: 'setLoadingMoreAgents', payload: false });
        if (payload?.onError) payload.onError(e);
      }
    },
    /**
     * Server-side agent search. Results are stored in a dedicated slice
     * (`agentSearchResults`) so the global `agents` list is never mutated by a
     * search. Supports its own pagination via `loadMore`.
     */
    *searchAgents({ payload }, { call, put, select }) {
      try {
        const isLoadingMore = payload?.loadMore;
        const { agentSearchPage, agentsPageSize, agentSearchTerm } = yield select(
          (state) => state.chat,
        );

        // On a new search the term comes in the payload; on loadMore reuse the
        // persisted term so pages stay scoped to the same query.
        const rawTerm = isLoadingMore
          ? agentSearchTerm
          : payload?.search !== undefined
          ? payload.search
          : agentSearchTerm;
        const term = (rawTerm || '').trim();

        // Empty term → drop search mode and show the full list again.
        if (!term) {
          yield put({ type: 'clearAgentSearch' });
          return;
        }

        if (!isLoadingMore) {
          yield put({ type: 'setAgentSearchTerm', payload: term });
          yield put({ type: 'setLoadingAgentSearch', payload: true });
        } else {
          yield put({ type: 'setLoadingMoreAgentSearch', payload: true });
        }

        const page = isLoadingMore ? agentSearchPage + 1 : 1;
        const skip = (page - 1) * agentsPageSize;
        const safeTerm = term.replace(/'/g, "''");
        const filter = `&$filter=contains(name,'${safeTerm}')`;
        const route = `ai/agent/allowed?$count=true&$skip=${skip}&$top=${agentsPageSize}${filter}&$select=id,name,isActive,description,iconUrl,provider`;

        const res = yield call(getAll, { route });

        // Ignore stale responses: a newer term may have been typed while this
        // request was in flight.
        const { agentSearchTerm: currentTerm } = yield select((state) => state.chat);
        if (!isLoadingMore && currentTerm !== term) return;

        if (isLoadingMore) {
          yield put({
            type: 'appendAgentSearch',
            payload: { ...res, page, pageSize: agentsPageSize },
          });
          yield put({ type: 'setLoadingMoreAgentSearch', payload: false });
        } else {
          yield put({
            type: 'saveAgentSearch',
            payload: { ...res, page, pageSize: agentsPageSize },
          });
          yield put({ type: 'setLoadingAgentSearch', payload: false });
        }
      } catch (e) {
        yield put({ type: 'setLoadingAgentSearch', payload: false });
        yield put({ type: 'setLoadingMoreAgentSearch', payload: false });
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchSpecificAgent({ payload }, { call, put, select }) {
      try {
        const { agentId } = payload;
        if (!agentId) return;

        const { agents } = yield select((state) => state.chat);

        // Check if agent is already loaded
        const existingAgent = agents.find((a) => a.id === agentId);
        if (existingAgent) {
          yield put({ type: 'setSelectedAgent', payload: existingAgent });
          if (payload?.onSuccess) payload.onSuccess(existingAgent);
          return;
        }

        // Load specific agent using OData filter
        const route = `ai/agent/allowed?$filter=id eq ${agentId}&$top=1&$select=id,name,isActive,description,iconUrl,provider`;
        const res = yield call(getAll, { route });

        if (res?.items?.length > 0) {
          const targetAgent = res.items[0];

          // Add agent to the list if not already present
          const updatedAgents = agents.find((a) => a.id === targetAgent.id)
            ? agents
            : [targetAgent, ...agents];

          const { totalAgents: prevTotal } = yield select((state) => state.chat);

          yield put({
            type: 'updateAgentsList',
            payload: {
              agents: updatedAgents,
              totalAgents: Math.max(prevTotal, updatedAgents.length),
            },
          });

          yield put({ type: 'setSelectedAgent', payload: targetAgent });

          if (payload?.onSuccess) payload.onSuccess(targetAgent);
        } else {
          if (payload?.onError) payload.onError(new Error('Agent not found'));
        }
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *checkAllowedAgents({ payload }, { call, put, select }) {
      try {
        // Prevent duplicate calls while already loading
        const { loadingAgents } = yield select((state) => state.chat);
        if (loadingAgents) {
          return;
        }

        const storedContext = getSessionItem(STORAGE_KEYS.USER_CONTEXT);
        let agentCount = 0;
        let hasStoredAgentCount = false;

        if (storedContext) {
          try {
            const parsed = JSON.parse(storedContext);
            if ('agentCount' in parsed) {
              agentCount = parsed.agentCount;
              hasStoredAgentCount = true;
            }
          } catch (e) {}
        }

        // Show the button optimistically from cache while we verify with API.
        // This prevents the button from being invisible during the network call.
        if (hasStoredAgentCount && agentCount > 0) {
          yield put({ type: 'setHasAllowedAgents', payload: true });
        }

        // Always fetch from API when Redux has no agents loaded. The cache
        // stores only the count, not the agent objects, so after a page
        // refresh Redux.agents is []. Also, a cached agentCount of 0 may be
        // stale (admin could have enabled agents since last visit), so we
        // re-verify with the server on every call.
        const { agentsPageSize, agents: currentAgents } = yield select((state) => state.chat);
        const needsFetch = currentAgents.length === 0;

        if (needsFetch) {
          yield put({ type: 'setLoadingAgents', payload: true });

          const route = `ai/agent/allowed?$count=true&$skip=0&$top=${agentsPageSize}&$select=id,name,isActive,description,iconUrl,provider`;
          const res = yield call(getAll, { route });

          agentCount = res?.totalCount || res?.items?.length || 0;

          const existingContext = storedContext ? JSON.parse(storedContext) : {};
          setSessionItem(
            STORAGE_KEYS.USER_CONTEXT,
            JSON.stringify({ ...existingContext, agentCount }),
          );

          if (agentCount > 0) {
            yield put({ type: 'saveAgents', payload: { ...res, page: 1 } });
          }

          yield put({ type: 'setLoadingAgents', payload: false });
        } else if (!hasStoredAgentCount) {
          // Agents are in Redux but no cache — derive count from loaded agents
          agentCount = currentAgents.length;
        }

        const hasAgents = agentCount > 0;
        yield put({ type: 'setHasAllowedAgents', payload: hasAgents });

        if (payload?.onSuccess) payload.onSuccess(hasAgents);
      } catch (e) {
        yield put({ type: 'setLoadingAgents', payload: false });
        // On error, preserve optimistic value if it was already set to true;
        // only set false if we never had a positive signal.
        const { hasAllowedAgents: current } = yield select((state) => state.chat);
        if (current !== true) {
          yield put({ type: 'setHasAllowedAgents', payload: false });
        }
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchCurrentChat({ payload }, { call, put, select }) {
      try {
        const res = yield call(getAll, payload);
        yield put({ type: 'saveCurrentChat', payload: res });

        // If response includes agentId, update selected agent
        if (res.agentId) {
          const { agents } = yield select((state) => state.chat);
          const agent = agents.find((a) => a.id === res.agentId);

          if (agent) {
            yield put({ type: 'setSelectedAgent', payload: agent });
          } else {
            // Agent not found in current list, fetch it specifically
            yield put({
              type: 'fetchSpecificAgent',
              payload: { agentId: res.agentId },
            });
          }
        }

        if (payload.onSuccess) payload.onSuccess(res);
      } catch (e) {}
    },
    *fetchThreads({ payload }, { call, put }) {
      try {
        const {
          agentId = null,
          page = 1,
          pageSize = 10,
          orderBy = 'updated desc',
          route: customRoute,
        } = payload || {};
        const skip = (page - 1) * pageSize;
        const route =
          customRoute ||
          (agentId
            ? `chat/thread/paged/${agentId}?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}`
            : `chat/thread/paged?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}`);
        const res = yield call(getAll, { route });
        yield put({ type: 'saveThreads', payload: res });
      } catch (e) {}
    },
    *fetchTasks({ payload }, { call, put }) {
      try {
        // Build route in model to keep components free of business logic
        const { chatThreadId, agentId, page = 1, pageSize = 10, orderBy = 'updated desc' } =
          payload || {};
        const skip = (page - 1) * pageSize;
        const encodedOrderBy = encodeURIComponent(orderBy);
        const operationFilter = encodeURIComponent(
          "operationType eq Automy.Core.Enums.Chat.ChatInternalTaskOperationType'Task'",
        );
        let route;
        if (chatThreadId) {
          const filter = encodeURIComponent(
            `chatThreadId eq ${chatThreadId} and operationType eq Automy.Core.Enums.Chat.ChatInternalTaskOperationType'Task'`,
          );
          route = `chat/task/paged?$filter=${filter}&$skip=${skip}&$top=${pageSize}&$orderBy=${encodedOrderBy}`;
        } else if (payload?.route) {
          route = payload.route; // fallback for backward compatibility
        } else if (agentId) {
          const baseRoute = `chat/task/paged/${agentId}`;
          route = `${baseRoute}?$filter=${operationFilter}&$skip=${skip}&$top=${pageSize}&$orderBy=${encodedOrderBy}`;
        } else {
          return;
        }

        const res = yield call(getAll, { route });
        yield put({ type: 'saveTasks', payload: res });
      } catch (e) {}
    },
    *fetchThreadById({ payload }, { call, put }) {
      try {
        const { threadId, expectedStatus, optimisticUpdated, onSuccess, onError } = payload || {};
        if (!threadId) return;

        const threadRes = yield call(getAll, { route: `chat/thread/${threadId}` });

        const normalizedUpdated =
          pickLatestIsoTimestamp(threadRes?.updated, optimisticUpdated) ||
          threadRes?.created ||
          new Date().toISOString();

        const mergedThread = withThreadTimestamps(
          {
            ...threadRes,
            status:
              expectedStatus !== undefined && expectedStatus !== null
                ? expectedStatus
                : threadRes.status,
            updated: normalizedUpdated,
          },
          optimisticUpdated || threadRes?.updated || threadRes?.created,
        );

        yield put({ type: 'upsertThread', payload: mergedThread });
        if (onSuccess) onSuccess(mergedThread);
      } catch (error) {
        if (payload?.onError) payload.onError(error);
      }
    },
    *executeTask({ payload }, { call, put }) {
      try {
        yield call(create, {
          route: `chat/task/execute/${payload.id}`,
          method: 'POST',
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *toggleTaskStatus({ payload }, { call, put }) {
      try {
        const action = payload.status === 1 ? 'pause' : 'resume';
        yield call(create, {
          route: `chat/task/${action}/${payload.id}`,
          method: 'POST',
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *deleteTask({ payload }, { call, put }) {
      try {
        yield call(remove, {
          route: `chat/task`,
          id: payload.id,
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchTaskRuns({ payload }, { call, put }) {
      try {
        const { taskId, page = 1, pageSize = 10, orderBy = 'created desc' } = payload || {};
        const skip = (page - 1) * pageSize;
        const route = `chat/task/runs/${taskId}/paged?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}`;
        const res = yield call(getAll, { route });
        yield put({ type: 'saveTaskRuns', payload: res });
        if (payload?.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchEmailTracking({ payload }, { call, put }) {
      try {
        const { threadId, onSuccess, onError } = payload || {};
        if (!threadId) return;

        const route = `chat/emailTracking/paged/${threadId}`;
        const res = yield call(getAll, { route });
        yield put({ type: 'saveEmailTracking', payload: res });
        if (onSuccess) onSuccess(res);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchRunDetails({ payload }, { call, put }) {
      try {
        const { taskId, runId } = payload || {};
        const route = `chat/task/runs/${taskId}/${runId}`;
        const res = yield call(getAll, { route });
        if (payload?.onSuccess) payload.onSuccess(res);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchMessages({ payload }, { call, put, select }) {
      try {
        const {
          threadId: threadIdParam,
          page = 1,
          pageSize = 10,
          orderBy = 'created desc',
          skipSaveCurrentChat,
        } = payload || {};
        const skip = (page - 1) * pageSize;
        const threadId =
          threadIdParam || (payload.route && payload.route.split('/')[2].split('?')[0]);
        const route = threadId
          ? `chat/messages/${threadId}?$skip=${skip}&$top=${pageSize}&$orderBy=${encodeURIComponent(
              orderBy,
            )}&$filter=messageType ne Automy.Core.Enums.Chat.ChatMessageType'UserJoinChat'&$select=id,chatThreadId,messageType,status,handlerType,senderType,userId,content,userName,errorMessage,relatedMessageId,created,updated`
          : payload.route;

        const res = yield call(getAll, { route });

        yield put({
          type: 'saveMessages',
          payload: { ...res, threadId },
        });

        // When called from openSpecificChat, the caller already set chatThread
        // with full data from the thread API; overwriting with the threads-list
        // version can lose fields like agentId
        if (!skipSaveCurrentChat) {
          const { threads } = yield select((state) => state.chat);
          let currentThread = threads.find((t) => t.id === threadId);
          if (!currentThread && res.thread) {
            currentThread = res.thread;
          }
          if (currentThread) {
            yield put({ type: 'saveCurrentChat', payload: currentThread });
          }
        }

        if (payload.onSuccess) payload.onSuccess(res);
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchMoreMessages({ payload }, { call, put, select }) {
      try {
        yield put({ type: 'setLoadingMore', payload: true });

        // Build route robustly to avoid /undefined when payload.route is absent
        const { page = 1, pageSize = 10, orderBy = 'created desc', threadId: payloadThreadId } =
          payload || {};

        // Determine threadId from payload or current state
        const state = yield select((s) => s.chat);
        const threadId = payloadThreadId || state.chatThread?.id || state.messagesThreadId;

        let route = payload?.route;
        if (!route) {
          if (!threadId) {
            // No route and no threadId -> nothing to load
            yield put({ type: 'setLoadingMore', payload: false });
            return;
          }
          const skip = (page - 1) * pageSize;
          route = `chat/messages/${threadId}?$skip=${skip}&$top=${pageSize}&$orderBy=${encodeURIComponent(
            orderBy,
          )}&$filter=messageType ne Automy.Core.Enums.Chat.ChatMessageType'UserJoinChat'&$select=id,chatThreadId,messageType,status,handlerType,senderType,userId,content,userName,errorMessage,relatedMessageId,created,updated`;
        }

        const res = yield call(getAll, { route });

        // Ensure pagination fields propagate to reducer
        const responseWithPaging = {
          ...res,
          page,
          pageSize,
        };
        yield put({ type: 'appendMessages', payload: responseWithPaging });
        yield put({ type: 'setLoadingMore', payload: false });
      } catch (e) {
        yield put({ type: 'setLoadingMore', payload: false });
      }
    },
    *queryUpdateMessages({ payload }, { put }) {
      yield put({ type: 'updateMessages', payload });
    },
    *fetchCloseChat({ payload }, { call, put, select }) {
      try {
        yield call(create, payload);
        // Reconcile with backend instead of blindly clearing chatThread.
        // Backend may not emit thread-status-changed for CloseChat, so we
        // refetch the thread to confirm the closed status and update the
        // threads list in place.
        const threadId =
          payload?.threadId || (payload?.route ? payload.route.split('/').pop() : null);
        if (threadId) {
          yield put({
            type: 'reconcileThread',
            payload: { threadId, expectedStatusHint: 4 },
          });
        }
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    /**
     * Reconcile a single thread against the backend (HTTP source of truth).
     * Used after critical actions whose socket event may not be emitted
     * (CloseChat, OpenChat, transfer*, takeover/release).
     *
     * Bounded retries handle Azure Hyperscale eventual consistency where the
     * thread may briefly show the previous status right after the action.
     */
    *reconcileThread({ payload }, { call, put }) {
      const {
        threadId,
        expectedStatusHint = null,
        attempt = 0,
        maxAttempts = 3,
        delayMs = 450,
        onSuccess,
        onError,
      } = payload || {};
      if (!threadId) return;

      try {
        const threadRes = yield call(getAll, { route: `chat/thread/${threadId}` });

        if (!threadRes || !threadRes.id) {
          if (onError) onError(new Error('Thread not found in reconciliation'));
          return;
        }

        const merged = withThreadTimestamps(threadRes, threadRes?.updated || threadRes?.created);
        yield put({ type: 'upsertThread', payload: merged });
        // If the active thread is the one we are reconciling, update it too.
        yield put({ type: 'syncCurrentChatIfMatch', payload: merged });

        const matchesExpected =
          expectedStatusHint === null ||
          expectedStatusHint === undefined ||
          Number(threadRes.status) === Number(expectedStatusHint);

        if (!matchesExpected && attempt < maxAttempts - 1) {
          yield call(sleep, delayMs);
          yield put({
            type: 'reconcileThread',
            payload: {
              threadId,
              expectedStatusHint,
              attempt: attempt + 1,
              maxAttempts,
              delayMs,
              onSuccess,
              onError,
            },
          });
          return;
        }

        if (onSuccess) onSuccess(merged);
      } catch (e) {
        if (attempt < maxAttempts - 1) {
          yield call(sleep, delayMs);
          yield put({
            type: 'reconcileThread',
            payload: {
              threadId,
              expectedStatusHint,
              attempt: attempt + 1,
              maxAttempts,
              delayMs,
              onSuccess,
              onError,
            },
          });
          return;
        }
        if (onError) onError(e);
      }
    },
    /**
     * Refetch the visible page of the threads list. Used:
     * - On socket reconnect (doc §11.7).
     * - When a thread-new-notification arrives for an unknown thread
     *   (doc §11.4 — covers backend gap of no thread-created event).
     */
    *reconcileThreadList({ payload }, { call, put }) {
      try {
        const {
          agentId = null,
          page = 1,
          pageSize = 10,
          orderBy = 'updated desc',
          searchValue = '',
          onSuccess,
          onError,
        } = payload || {};
        const skip = (page - 1) * pageSize;
        const trimmedSearch = (searchValue || '').trim();
        const searchFilter = trimmedSearch
          ? `&$filter=contains(name,'${trimmedSearch}') or contains(title,'${trimmedSearch}')`
          : '';
        const route = agentId
          ? `chat/thread/paged/${agentId}?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}${searchFilter}`
          : `chat/thread/paged?$skip=${skip}&$top=${pageSize}&$orderBy=${orderBy}${searchFilter}`;

        const res = yield call(getAll, { route });
        yield put({ type: 'saveThreads', payload: res });
        if (onSuccess) onSuccess(res);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    /**
     * Ensure a given threadId exists in state.threads. If missing, fetch it
     * by id and upsert. Used when an unknown thread-new-notification arrives
     * (covers backend gap of no thread-created/thread-assigned event).
     */
    *ensureThreadInList({ payload }, { call, put, select }) {
      try {
        const { threadId, onSuccess, onError } = payload || {};
        if (!threadId) return;

        const { threads } = yield select((state) => state.chat);
        const exists = (threads || []).some(
          (t) => t?.id?.toString() === String(threadId).toString(),
        );
        if (exists) {
          if (onSuccess) onSuccess(null);
          return;
        }

        const threadRes = yield call(getAll, { route: `chat/thread/${threadId}` });
        if (!threadRes || !threadRes.id) {
          if (onError) onError(new Error('Thread not found'));
          return;
        }

        const merged = withThreadTimestamps(threadRes, threadRes?.updated || threadRes?.created);
        yield put({ type: 'upsertThread', payload: merged });
        if (onSuccess) onSuccess(merged);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },
    *fetchOpenChat({ payload }, { call, put }) {
      yield put({ type: 'queryOpenChat', payload });
    },
    *changeThreadTitle({ payload }, { call, put }) {
      try {
        yield call(create, {
          route: `chat/thread/changeTitle/${payload.id}/${encodeURIComponent(payload.title)}`,
          method: 'POST',
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *deleteThread({ payload }, { call, put }) {
      try {
        yield call(remove, {
          route: `chat/thread`,
          id: payload.id,
        });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchReopenChat({ payload }, { call, put }) {
      try {
        yield call(create, { route: payload.route, method: 'POST' });

        // Extract threadId from route (chat/openChat/threadId)
        const threadId = payload.threadId || payload.route.split('/').pop();

        // Reconcile thread status against backend (HTTP source of truth) — backend
        // does not always emit thread-status-changed for OpenChat (doc §8.4 / §11.3).
        yield put({
          type: 'reconcileThread',
          payload: {
            threadId,
            // Active = 2; backend may also keep it Pending=1 if no agent yet.
            // We don't pin expectedStatusHint so any non-closed status passes first try.
            expectedStatusHint: null,
            onSuccess: (merged) => {
              if (payload.onReconciled) payload.onReconciled(merged);
            },
          },
        });

        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *fetchNewChat({ payload }, { call, put }) {
      try {
        yield call(create, { route: payload.route, method: 'POST' });
        if (payload.onSuccess) payload.onSuccess();
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *changeAgent({ payload }, { call, put, select }) {
      try {
        const { agentId, chatType } = payload;
        const route = agentId ? `chat/current/${chatType}/${agentId}` : `chat/current/${chatType}`;

        const res = yield call(getAll, { route });
        yield put({ type: 'saveCurrentChat', payload: res });

        // Ensure the correct agent is selected after changing
        if (agentId) {
          const { agents } = yield select((state) => state.chat);
          const agent = agents.find((a) => a.id === agentId);

          if (agent) {
            yield put({ type: 'setSelectedAgent', payload: agent });
          } else {
            // Agent not found in current list, fetch it specifically
            yield put({
              type: 'fetchSpecificAgent',
              payload: { agentId: agentId },
            });
          }
        }

        if (payload.onSuccess) payload.onSuccess(res);
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
    *openSpecificChat({ payload }, { call, put, select }) {
      try {
        const { threadId, agentId } = payload;

        // Stamp this invocation so we can detect if a newer one superseded us
        const invocationTs = Date.now();
        yield put({ type: 'setOpenSpecificChatTs', payload: invocationTs });

        // First, get specific thread data
        const threadRes = yield call(getAll, { route: `chat/thread/${threadId}` });

        // Abort if a newer openSpecificChat was dispatched while we were waiting
        const { openSpecificChatTs: tsAfterFetch } = yield select((state) => state.chat);
        if (tsAfterFetch !== invocationTs) return;

        // Resolve agent: prefer explicit agentId, then threadRes.agentId
        const finalAgentId = agentId || threadRes.agentId;
        if (finalAgentId) {
          const { agents } = yield select((state) => state.chat);
          let targetAgent = agents.find((a) => a.id === finalAgentId);

          if (agents.length === 0) {
            const agentsRes = yield call(getAll, {
              route: 'ai/agent/allowed?$select=id,name,isActive,description,iconUrl,provider',
            });
            yield put({ type: 'saveAgents', payload: agentsRes });
            const updatedAgents = agentsRes.items || agentsRes || [];
            targetAgent = updatedAgents.find((a) => a.id === finalAgentId);
          }

          if (targetAgent) {
            yield put({ type: 'setSelectedAgent', payload: targetAgent });
          } else {
            yield put({
              type: 'fetchSpecificAgent',
              payload: { agentId: finalAgentId },
            });
          }
        }

        // Abort if superseded during agent resolution
        const { openSpecificChatTs: tsAfterAgent } = yield select((state) => state.chat);
        if (tsAfterAgent !== invocationTs) return;

        // Set current thread BEFORE opening chat to prevent fetchCurrentChat from firing.
        // saveCurrentChat resets messagesThreadId when the thread changes, which
        // unblocks the stale-response guard in saveMessages for the new thread.
        yield put({ type: 'saveCurrentChat', payload: threadRes });

        // Open chat
        yield put({ type: 'setIsOpen', payload: true });

        // Small delay to ensure React has re-rendered and refs are updated
        yield new Promise((resolve) => setTimeout(resolve, 200));

        // Final staleness check before joining and fetching messages
        const { openSpecificChatTs: tsAfterDelay } = yield select((state) => state.chat);
        if (tsAfterDelay !== invocationTs) return;

        // Force joinChat from the component; resetManualAgent ensures the
        // auto-select agent effect is not blocked by a previous manual change.
        // Pass threadStatus so the handler can override stale closure values.
        const forceJoinDetail = {
          threadId,
          resetManualAgent: true,
          threadStatus: threadRes?.status,
        };
        window.dispatchEvent(new CustomEvent('forceJoinAutomyChat', { detail: forceJoinDetail }));

        // Load thread messages; skipSaveCurrentChat prevents fetchMessages
        // from overwriting the accurate chatThread we just set
        yield put({
          type: 'fetchMessages',
          payload: {
            threadId,
            skipSaveCurrentChat: true,
          },
        });

        if (payload.onSuccess) payload.onSuccess({ threadId, agentId: finalAgentId });
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },

    // Thread Participants Management
    *fetchThreadParticipants({ payload }, { call, put }) {
      try {
        const { chatThreadId, page = 1, pageSize = 10 } = payload;
        const skip = (page - 1) * pageSize;
        const route = `chat/thread/participants/paged/${chatThreadId}?$skip=${skip}&$top=${pageSize}`;

        const response = yield call(getAll, { route });
        const responseItems =
          response?.items ||
          response?.data?.items ||
          response?.data?.data?.items ||
          (Array.isArray(response) ? response : []) ||
          [];
        const participants = Array.isArray(responseItems)
          ? responseItems
          : responseItems?.items || [];
        const totalCount =
          response?.totalCount ||
          response?.data?.totalCount ||
          response?.data?.data?.totalCount ||
          response?.count ||
          participants.length;

        yield put({
          type: 'saveThreadParticipants',
          payload: {
            chatThreadId,
            participants,
            totalCount,
          },
        });

        if (payload.onSuccess) payload.onSuccess({ items: participants, totalCount });
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },

    *addThreadParticipant({ payload }, { call, put }) {
      try {
        const { chatThreadId, userId, users, accessMode, onSuccess, onError } = payload;
        // Support both a single id (legacy) and an array of ids (new API)
        const usersArray = Array.isArray(users) ? users.filter(Boolean) : userId ? [userId] : [];

        if (!chatThreadId || !accessMode || usersArray.length === 0) {
          if (onError) onError(new Error('Invalid payload for adding participants'));
          return;
        }

        const route = `chat/thread/participants/add/${chatThreadId}/${accessMode}`;

        const response = yield call(create, {
          route,
          method: 'POST',
          data: { users: usersArray },
        });

        // Single delayed refresh to reconcile (2s)
        yield call(sleep, 2000);
        yield put({ type: 'fetchThreadParticipants', payload: { chatThreadId } });

        if (onSuccess) onSuccess(response);
      } catch (e) {
        if (payload?.onError) payload.onError(e);
      }
    },

    *updateThreadParticipant({ payload }, { call, put, select }) {
      try {
        const { participantId, newAccessMode, chatThreadId } = payload;
        const route = `chat/thread/participants/change/${participantId}/${newAccessMode}`;

        // Use direct request with full route to avoid duplicating the participantId in the URL
        const response = yield call(create, {
          route,
          method: 'PATCH',
        });

        // Conditional polling: stop early as soon as backend reflects the change
        if (chatThreadId) {
          const delays = [0, 200, 600, 1200];
          for (let i = 0; i < delays.length; i += 1) {
            const wait = delays[i];
            if (wait > 0) {
              yield call(sleep, wait);
            }
            // Direct read to ensure we check latest backend state
            const pollRoute = `chat/thread/participants/paged/${chatThreadId}?$skip=0&$top=10`;
            const pollResponse = yield call(getAll, { route: pollRoute });

            const items = pollResponse.items || [];
            const matched = items.some(
              (p) => p.id === participantId && Number(p.accessMode) === Number(newAccessMode),
            );

            // Only update the store when the backend reflects the new state
            if (matched || i === delays.length - 1) {
              yield put({
                type: 'saveThreadParticipants',
                payload: {
                  chatThreadId,
                  participants: items,
                  totalCount: pollResponse.totalCount || 0,
                },
              });
              if (matched) {
                break;
              }
            }
          }
        }

        if (payload.onSuccess) payload.onSuccess(response);
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },

    *removeThreadParticipant({ payload }, { call, put }) {
      try {
        const { participantId, chatThreadId } = payload;
        const route = `chat/thread/participants`;

        const response = yield call(remove, { id: participantId, route });

        // Do not refresh on success; UI already applied optimistic removal

        if (payload.onSuccess) payload.onSuccess(response);
      } catch (e) {
        if (payload.onError) payload.onError(e);
      }
    },
  },
  reducers: {
    saveCurrentChat(state, { payload }) {
      if (!payload) {
        return state;
      }

      const normalizedControlMode =
        payload.controlMode !== undefined && payload.controlMode !== null
          ? payload.controlMode
          : state.chatThread?.controlMode !== undefined && state.chatThread?.controlMode !== null
          ? state.chatThread.controlMode
          : ChatControlMode?.Auto || 1;

      const threadChanged =
        payload.id &&
        state.chatThread?.id &&
        String(payload.id).toLowerCase() !== String(state.chatThread.id).toLowerCase();

      return {
        ...state,
        chatThread: {
          ...payload,
          controlMode: normalizedControlMode,
        },
        // When switching threads, reset messagesThreadId so the stale-response
        // guard in saveMessages accepts messages for the new thread.
        // Messages from the previous thread stay visible briefly until the new
        // fetch resolves — this avoids an empty intermediate state that can crash
        // components that reference the last message.
        ...(threadChanged && { messagesThreadId: null }),
      };
    },
    saveThreads(state, { payload }) {
      const existingThreadsMap = (state.threads || []).reduce((acc, thread) => {
        if (thread?.id !== undefined && thread?.id !== null) {
          acc[thread.id.toString()] = thread;
        }
        return acc;
      }, {});

      const threadsArray = ((payload && payload.items) || []).map((thread) => {
        const stringId =
          thread?.id !== undefined && thread?.id !== null ? thread.id.toString() : null;
        const existing = stringId ? existingThreadsMap[stringId] : null;
        const normalizedControlMode =
          thread?.controlMode !== undefined && thread?.controlMode !== null
            ? thread.controlMode
            : existing?.controlMode !== undefined && existing?.controlMode !== null
            ? existing.controlMode
            : ChatControlMode?.Auto || 1;
        return withThreadTimestamps(
          {
            ...thread,
            controlMode: normalizedControlMode,
          },
          existing?.updated || existing?.created,
        );
      });

      const sortedThreads = threadsArray.sort((a, b) => {
        const dateA = a?.updatedMs ?? getTimestampMs(a?.updated || a?.created);
        const dateB = b?.updatedMs ?? getTimestampMs(b?.updated || b?.created);
        return dateB - dateA;
      });

      return {
        ...state,
        threads: sortedThreads,
        totalThreads: payload.totalCount || 0, // Use separate field for threads
        // totalCount is preserved for messages
      };
    },
    syncCurrentChatIfMatch(state, { payload }) {
      if (!payload?.id) return state;
      const currentId = state.chatThread?.id;
      if (!currentId) return state;
      if (String(currentId).toLowerCase() !== String(payload.id).toLowerCase()) return state;

      const normalizedControlMode =
        payload.controlMode !== undefined && payload.controlMode !== null
          ? payload.controlMode
          : state.chatThread?.controlMode;
      const normalizedHandlerType =
        payload.handlerType !== undefined && payload.handlerType !== null
          ? payload.handlerType
          : state.chatThread?.handlerType;

      // CRITICAL: preserve the original id (and its casing). Several views look
      // up state by exact id (aiThinkingTimelineByThread[chatThread.id],
      // aiThinkingByThread[id], messagesThreadId comparisons). If we swap the
      // id with whatever casing the backend returns, those lookups silently
      // miss and the chat appears "frozen" while messages keep streaming.
      // Same goes for status — keep the numeric form to avoid mixing string
      // ("closed") with number (4) across the codebase.
      const stableId = state.chatThread?.id || payload.id;
      const stableStatus = (() => {
        if (payload.status === undefined || payload.status === null) {
          return state.chatThread?.status;
        }
        if (typeof payload.status === 'number') return payload.status;
        const map = { pending: 1, active: 2, 'looking-for-agent': 3, closed: 4, archived: 5 };
        const lc = String(payload.status).toLowerCase();
        return map[lc] !== undefined ? map[lc] : payload.status;
      })();

      return {
        ...state,
        chatThread: {
          ...state.chatThread,
          ...payload,
          id: stableId,
          status: stableStatus,
          controlMode: normalizedControlMode,
          handlerType: normalizedHandlerType,
        },
      };
    },
    upsertThread(state, { payload }) {
      const threads = [...state.threads];
      const incomingId = payload?.id ? String(payload.id) : null;
      // Case-insensitive match — backend may send ids in different casing
      // depending on the endpoint (paged list vs. by-id). Mixing casing breaks
      // lookups in localThreads and the timeline maps keyed by id.
      const existingIndex = incomingId
        ? threads.findIndex((t) => String(t.id).toLowerCase() === incomingId.toLowerCase())
        : -1;
      const existingThread = existingIndex !== -1 ? threads[existingIndex] : null;

      // Preserve existing id/casing when merging so downstream consumers keep
      // matching against the original key.
      const baseThread = existingThread
        ? { ...existingThread, ...payload, id: existingThread.id }
        : payload;
      const mergedThread = withThreadTimestamps(
        baseThread,
        existingThread?.updated || existingThread?.created,
      );

      const safeUpdated = pickLatestIsoTimestamp(
        mergedThread.updated || mergedThread.created,
        existingThread?.updated || existingThread?.created,
      );
      mergedThread.updated = safeUpdated || new Date().toISOString();
      mergedThread.updatedMs = getTimestampMs(mergedThread.updated);

      if (existingIndex !== -1) {
        threads[existingIndex] = mergedThread;
      } else {
        threads.unshift(mergedThread);
      }

      threads.sort((a, b) => {
        const dateA = a?.updatedMs ?? getTimestampMs(a?.updated || a?.created);
        const dateB = b?.updatedMs ?? getTimestampMs(b?.updated || b?.created);
        return dateB - dateA;
      });

      const nextState = {
        ...state,
        threads,
      };

      return nextState;
    },
    saveTasks(state, { payload }) {
      const tasks =
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload?.value) && payload.value) ||
        (Array.isArray(payload) ? payload : []) ||
        [];
      const tasksCount =
        payload?.totalCount ?? payload?.count ?? payload?.items?.length ?? tasks.length ?? 0;

      return {
        ...state,
        tasks,
        tasksCount,
      };
    },
    clearTasks(state) {
      return {
        ...state,
        tasks: [],
        tasksCount: 0,
      };
    },
    updateTaskStatus(state, { payload }) {
      const { taskId, status } = payload;

      // Update the task in the list of tasks if it exists
      const updatedTasks = state.tasks.map((task) => {
        if (task.id === taskId) {
          return {
            ...task,
            status: status,
            updated: new Date().toISOString(),
          };
        }
        return task;
      });

      return {
        ...state,
        tasks: updatedTasks,
      };
    },
    saveTaskRuns(state, { payload }) {
      return {
        ...state,
        taskRuns: payload.items || [],
        taskRunsCount: payload.totalCount || 0,
      };
    },
    clearTaskRuns(state) {
      return {
        ...state,
        taskRuns: [],
        taskRunsCount: 0,
      };
    },
    saveEmailTracking(state, { payload }) {
      const emails =
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload?.value) && payload.value) ||
        (Array.isArray(payload) ? payload : []) ||
        [];
      const emailTrackingCount =
        payload?.totalCount ?? payload?.count ?? payload?.items?.length ?? emails.length ?? 0;

      return {
        ...state,
        emailTracking: emails,
        emailTrackingCount,
      };
    },
    saveMessages(state, { payload }) {
      if (!payload || !payload.items) {
        return state;
      }

      const toMs = (iso) => {
        if (!iso) return 0;
        const s = String(iso);
        return (s.includes('Z') || s.includes('+') ? new Date(s) : new Date(`${s}Z`)).getTime();
      };

      // Extract threadId from response, route-provided payload.threadId, or first message
      const threadId =
        payload.threadId ||
        (payload.items[0] && (payload.items[0].chatThreadId || payload.items[0].threadId)) ||
        state.chatThread?.id;

      // Guard: discard stale responses from a previously active thread
      if (state.messagesThreadId && threadId) {
        if (state.messagesThreadId.toString() !== threadId.toString()) {
          return state;
        }
      } else if (!state.messagesThreadId && threadId && state.chatThread?.id) {
        // messagesThreadId is null (thread just switched via saveCurrentChat).
        // Only accept messages for the thread that is currently active.
        if (state.chatThread.id.toString() !== threadId.toString()) {
          return state;
        }
      }

      // Get messages from buffer for this threadId (lowercase key for consistency)
      const bufferKey = threadId ? String(threadId).toLowerCase() : null;
      const bufferedMessages = bufferKey ? state.messageBuffer[bufferKey] || [] : [];

      // Filter out automatic welcome messages from the backend
      const filteredMessages = filterAutomaticWelcomeMessages(payload.items);

      // Combine filtered messages with buffer messages AND any socket-delivered
      // messages already in state.messages, avoiding duplicates.
      // Socket messages added by receiveSocketMessage live only in state.messages
      // (not in the buffer). Without this merge they would be silently overwritten
      // when the API fetch resolves moments later with data that doesn't yet
      // include the just-received socket message.
      const allMessagesRaw = [];
      const seenKeys = new Set();
      [...filteredMessages, ...bufferedMessages].forEach((msg, idx) => {
        const key = getMessageUniqueKey(msg) || `__idx_${idx}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          allMessagesRaw.push(msg);
        }
      });
      // Preserve socket-delivered messages from state.messages that the API
      // hasn't returned yet (race: socket arrives before API indexes it).
      // Only keep messages belonging to the same thread to avoid leaking
      // old messages from a previously active thread.
      const threadIdLower = threadId ? String(threadId).toLowerCase() : null;
      state.messages.forEach((existingMsg) => {
        const key = getMessageUniqueKey(existingMsg);
        if (!key || seenKeys.has(key)) return;
        const msgThread = existingMsg.threadId || existingMsg.chatThreadId;
        if (threadIdLower && msgThread && String(msgThread).toLowerCase() !== threadIdLower) return;
        seenKeys.add(key);
        allMessagesRaw.push(existingMsg);
      });

      // Normalize and cache createdMs for efficient sorting
      const allMessages = allMessagesRaw.map((m) => {
        if (m && typeof m.createdMs !== 'number') {
          return { ...m, createdMs: toMs(m.created) };
        }
        return m;
      });

      // Preserve totalCount if it's higher than the new value (infinite scroll protection)
      // This prevents FullscreenChat fetches from resetting the totalCount incorrectly
      const shouldPreserveTotalCount =
        state.totalCount > 0 &&
        payload.totalCount > 0 &&
        state.totalCount > payload.totalCount &&
        state.messagesThreadId === threadId; // Only preserve for same thread

      const finalMessages = allMessages.sort((a, b) => getOrderTs(b) - getOrderTs(a));

      return {
        ...state,
        messages: finalMessages,
        totalCount: shouldPreserveTotalCount ? state.totalCount : payload.totalCount,
        page: payload.page,
        pageSize: payload.pageSize,
        messagesThreadId: threadId,
        // The new thread's messages are now loaded — the agent switch is
        // complete, so stop force-buffering incoming socket messages.
        agentSwitching: false,
        // Clear the buffer for this threadId (lowercase key for consistency)
        messageBuffer: {
          ...state.messageBuffer,
          ...(bufferKey ? { [bufferKey]: [] } : {}),
        },
      };
    },
    appendMessages(state, { payload }) {
      if (!payload || !payload.items || payload.items.length === 0) {
        return state;
      }

      // Guard: if a threadId is provided, discard if it doesn't match the current thread
      const payloadThreadId =
        payload.threadId ||
        (payload.items[0] && (payload.items[0].chatThreadId || payload.items[0].threadId));
      if (
        payloadThreadId &&
        state.messagesThreadId &&
        payloadThreadId.toString() !== state.messagesThreadId.toString()
      ) {
        return state;
      }

      const toMs = (iso) => {
        if (!iso) return 0;
        const s = String(iso);
        return (s.includes('Z') || s.includes('+') ? new Date(s) : new Date(`${s}Z`)).getTime();
      };

      // Filter out automatic welcome messages from the backend
      const filteredNewMessages = filterAutomaticWelcomeMessages(payload.items);

      // Filter duplicate messages
      const newMessages = filteredNewMessages.filter((newMsg) => {
        const newKey = getMessageUniqueKey(newMsg);
        if (!newKey) {
          return !state.messages.some((existingMsg) => !getMessageUniqueKey(existingMsg));
        }
        return !state.messages.some((existingMsg) => getMessageUniqueKey(existingMsg) === newKey);
      });

      // Combine existing messages with new ones
      const combinedRaw = [...newMessages, ...state.messages];
      const combinedUnique = [];
      const seenCombined = new Set();
      combinedRaw.forEach((msg, idx) => {
        const key = getMessageUniqueKey(msg) || `__idx_${idx}`;
        if (!seenCombined.has(key)) {
          seenCombined.add(key);
          combinedUnique.push(msg);
        }
      });
      // Ensure all have createdMs for efficient sorting
      const combinedMessages = combinedUnique
        .map((m) => {
          if (m && typeof m.createdMs !== 'number') {
            return { ...m, createdMs: toMs(m.created) };
          }
          return m;
        })
        .sort((a, b) => getOrderTs(b) - getOrderTs(a));

      // Preserve totalCount if it's higher than the new value (infinite scroll protection)
      const shouldPreserveTotalCount =
        state.totalCount > 0 && payload.totalCount > 0 && state.totalCount > payload.totalCount;

      return {
        ...state,
        messages: combinedMessages,
        totalCount: shouldPreserveTotalCount ? state.totalCount : payload.totalCount,
        page: payload.page,
        pageSize: payload.pageSize,
      };
    },
    setLoadingMore(state, { payload }) {
      return {
        ...state,
        loadingMore: payload,
      };
    },
    setAgentSwitching(state, { payload }) {
      return { ...state, agentSwitching: Boolean(payload) };
    },
    clearMessages(state, { payload = {} }) {
      // When switching agents we move away from the current thread entirely, so
      // any buffered (late socket) messages for ANY thread must be dropped to
      // avoid them resurfacing as stale "cached" messages later. Callers opt in
      // via `clearAllBuffers`.
      if (payload.clearAllBuffers) {
        return {
          ...state,
          messages: [],
          totalCount: 0,
          page: 1,
          pageSize: 10,
          messagesThreadId: null,
          messageBuffer: {},
        };
      }

      // Preserve buffer for current thread if specified (lowercase key for consistency)
      const currentThreadId = payload.preserveBufferForThread || state.chatThread?.id;
      const bufferKey = currentThreadId ? String(currentThreadId).toLowerCase() : null;
      const preservedBuffer =
        bufferKey && state.messageBuffer[bufferKey]
          ? { [bufferKey]: state.messageBuffer[bufferKey] }
          : {};

      return {
        ...state,
        messages: [],
        totalCount: 0,
        page: 1,
        pageSize: 10,
        messagesThreadId: null, // Also clear messagesThreadId to avoid sync issues
        messageBuffer: preservedBuffer, // Preserve buffer for current thread only
      };
    },
    ensureMessagesThreadId(state, { payload }) {
      const threadId = payload?.threadId;
      if (!threadId) return state;
      if (
        state.messagesThreadId &&
        String(state.messagesThreadId).toLowerCase() === String(threadId).toLowerCase()
      ) {
        return state;
      }
      // Accept if the active chatThread matches OR if chatThread is empty
      // (the component already verified the threadId before dispatching).
      const currentId = state.chatThread?.id;
      const chatThreadMatches =
        !currentId || String(currentId).toLowerCase() === String(threadId).toLowerCase();
      if (chatThreadMatches) {
        return { ...state, messagesThreadId: threadId };
      }
      return state;
    },
    flushMessageBuffer(state, { payload }) {
      const threadId = payload?.threadId || state.chatThread?.id;
      if (!threadId) return state;

      const bufferKey = String(threadId).toLowerCase();
      const buffered = state.messageBuffer[bufferKey] || [];
      if (buffered.length === 0) return state;

      let updatedMessages = [...state.messages];
      const seenKeys = new Set(updatedMessages.map((m) => getMessageUniqueKey(m)).filter(Boolean));

      const flushNow = Date.now();
      buffered.forEach((msg) => {
        const key = getMessageUniqueKey(msg);
        if (key && seenKeys.has(key)) return;
        if (key) seenKeys.add(key);

        const normalizedId =
          msg.id || msg.messageId || `buf-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        const enriched = {
          ...msg,
          id: normalizedId,
          created: msg.created || new Date().toISOString(),
          createdMs: isoToMs(msg.created || new Date().toISOString()),
          // Buffered messages came via socket originally; tag them with an
          // arrival timestamp so they sort after older messages even if the
          // backend stamped them with a stale `created`.
          socketReceivedAt:
            typeof msg.socketReceivedAt === 'number' ? msg.socketReceivedAt : flushNow,
        };
        if (!enriched.messageId) {
          enriched.messageId = normalizedId;
        }

        const ts = getOrderTs(enriched);
        let insertAt = 0;
        while (insertAt < updatedMessages.length && getOrderTs(updatedMessages[insertAt]) > ts) {
          insertAt++;
        }
        updatedMessages.splice(insertAt, 0, enriched);
      });

      return {
        ...state,
        messages: updatedMessages,
        messagesThreadId: state.messagesThreadId || threadId,
        messageBuffer: { ...state.messageBuffer, [bufferKey]: [] },
      };
    },
    updateMessages(state, { payload }) {
      const enriched =
        payload && typeof payload.createdMs !== 'number'
          ? { ...payload, createdMs: isoToMs(payload.created) }
          : payload;

      if (enriched.messageType === 'response') {
        const thinkingIndex = state.messages.findIndex(
          (message) =>
            message.messageType === 'agent-typing' || message.messageType === 'ai-thinking',
        );

        if (thinkingIndex !== -1) {
          const newMessages = [...state.messages];
          newMessages[thinkingIndex] = enriched;
          return {
            ...state,
            messages: newMessages,
          };
        }
      }

      // Check if message with same ID already exists (avoid duplicates)
      const existingIndex = state.messages.findIndex((msg) => msg.id === enriched.id);
      if (existingIndex !== -1) {
        const newMessages = [...state.messages];
        newMessages[existingIndex] = enriched;
        return {
          ...state,
          messages: newMessages,
        };
      }

      // Insert in sorted position (desc by ORDER ts) to maintain invariant
      const ts = getOrderTs(enriched);
      let insertIdx = 0;
      while (insertIdx < state.messages.length && getOrderTs(state.messages[insertIdx]) > ts) {
        insertIdx++;
      }
      const newMessages = [...state.messages];
      newMessages.splice(insertIdx, 0, enriched);
      return { ...state, messages: newMessages };
    },
    removeOptimisticMessage(state, { payload }) {
      const { messageId } = payload || {};
      if (!messageId) return state;
      const filtered = state.messages.filter(
        (msg) => !(msg.isOptimistic && (msg.id === messageId || msg.messageId === messageId)),
      );
      if (filtered.length === state.messages.length) return state;
      return { ...state, messages: filtered };
    },
    queryOpenChat(state, { payload }) {
      return {
        ...state,
        isOpen: payload,
      };
    },
    // New reducer to handle socket messages
    receiveSocketMessage(state, { payload }) {
      const messageThreadId = payload.threadId;
      const bufferKey = messageThreadId ? String(messageThreadId).toLowerCase() : null;
      // Use the component-verified threadId as fallback when state.chatThread?.id
      // is momentarily null/stale due to Redux timing (race condition).
      const currentThreadId = state.chatThread?.id || payload._verifiedCurrentThreadId;

      const threadMismatch =
        !messageThreadId ||
        !currentThreadId ||
        String(messageThreadId).toLowerCase() !== String(currentThreadId).toLowerCase();

      const isIncomingExternalMessage =
        payload.messageType === 'whatsapp-message' ||
        payload.messageType === 17 ||
        payload.messageType === CoreEnums?.ChatMessageType?.WhatsAppMessage ||
        payload.messageType === 'email-message' ||
        payload.messageType === 14 ||
        payload.messageType === CoreEnums?.ChatMessageType?.EmailMessage;

      if (threadMismatch) {
        // For response-type and incoming external messages, buffer instead of
        // silently discarding. During thread switches currentThreadId can be
        // momentarily null/stale; buffering ensures the message surfaces once
        // the thread loads.
        if (bufferKey) {
          const isResponseType =
            payload.messageType === 'response' ||
            payload.messageType === 6 ||
            payload.messageType === CoreEnums?.ChatMessageType?.Response ||
            payload.messageType === 'engine-ai-message' ||
            payload.messageType === 13 ||
            payload.messageType === CoreEnums?.ChatMessageType?.EngineAIMessage;

          if (isResponseType || isIncomingExternalMessage) {
            const existingBuffer = state.messageBuffer[bufferKey] || [];
            const incomingKey = getMessageUniqueKey(payload);
            if (incomingKey && existingBuffer.some((m) => getMessageUniqueKey(m) === incomingKey)) {
              return state;
            }
            const MAX_BUFFER_PER_THREAD = 50;
            const newBuffer =
              existingBuffer.length >= MAX_BUFFER_PER_THREAD
                ? [...existingBuffer.slice(-MAX_BUFFER_PER_THREAD + 1), payload]
                : [...existingBuffer, payload];
            return {
              ...state,
              messageBuffer: { ...state.messageBuffer, [bufferKey]: newBuffer },
            };
          }
        }
        return state;
      }

      // If threadId is active but messages haven't been loaded yet, buffer
      // non-response messages. Response/AI/WhatsApp/email messages skip the
      // buffer so they always appear immediately (the buffer may never be
      // flushed if no subsequent fetchMessages runs).
      const messagesLoaded =
        state.messagesThreadId != null &&
        currentThreadId != null &&
        String(state.messagesThreadId).toLowerCase() === String(currentThreadId).toLowerCase();

      if (!messagesLoaded) {
        const isResponseType =
          payload.messageType === 'response' ||
          payload.messageType === 6 ||
          payload.messageType === CoreEnums?.ChatMessageType?.Response ||
          payload.messageType === 'engine-ai-message' ||
          payload.messageType === 13 ||
          payload.messageType === CoreEnums?.ChatMessageType?.EngineAIMessage;

        // During an agent switch the list was just cleared and chatThread may
        // still point at the previous thread for a tick. Buffer EVERY type
        // (including response/external) so a late socket message can't flash
        // into the cleared list; it surfaces only when the matching thread's
        // messages load (saveMessages flushes its buffer).
        if (state.agentSwitching || (!isResponseType && !isIncomingExternalMessage)) {
          const existingBuffer = bufferKey ? state.messageBuffer[bufferKey] || [] : [];
          const incomingKey = getMessageUniqueKey(payload);
          if (incomingKey) {
            const alreadyBuffered = existingBuffer.some(
              (m) => getMessageUniqueKey(m) === incomingKey,
            );
            if (alreadyBuffered) return state;
          }
          const MAX_BUFFER_PER_THREAD = 50;
          const newBuffer =
            existingBuffer.length >= MAX_BUFFER_PER_THREAD
              ? [...existingBuffer.slice(-MAX_BUFFER_PER_THREAD + 1), payload]
              : [...existingBuffer, payload];
          const bufferKeys = Object.keys(state.messageBuffer);
          let trimmedBuffer = bufferKey
            ? { ...state.messageBuffer, [bufferKey]: newBuffer }
            : { ...state.messageBuffer };
          if (bufferKey && bufferKeys.length > 20 && !state.messageBuffer[bufferKey]) {
            const oldest = bufferKeys[0];
            delete trimmedBuffer[oldest];
          }
          return {
            ...state,
            messageBuffer: trimmedBuffer,
          };
        }
        // Response-type and incoming external messages fall through to be added directly
      }

      // Normalize the ID of the message (may come as 'id' or 'messageId')
      const normalizedId =
        payload.id ||
        payload.messageId ||
        `socket-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

      const normalizeErrorArray = (collection) => {
        if (!Array.isArray(collection)) {
          return '';
        }
        return collection
          .map((entry) => {
            if (!entry) {
              return '';
            }
            if (typeof entry === 'string') {
              return entry;
            }
            if (typeof entry === 'object' && entry.message) {
              return entry.message;
            }
            return String(entry);
          })
          .filter((entry) => entry && entry.trim())
          .join('\n')
          .trim();
      };

      const resolveStatusValue = (...candidates) => {
        for (let i = 0; i < candidates.length; i += 1) {
          const candidate = candidates[i];
          if (candidate === undefined || candidate === null || candidate === '') {
            continue;
          }
          return candidate;
        }
        return null;
      };

      const normalizedContent = payload?.content ? { ...payload.content } : undefined;
      const resolvedStatus = resolveStatusValue(
        payload?.status,
        payload?.messageStatus,
        payload?.chatMessageStatus,
        payload?.data?.status,
        payload?.content?.status,
      );

      if (
        normalizedContent &&
        normalizedContent.status === undefined &&
        resolvedStatus !== null &&
        resolvedStatus !== undefined
      ) {
        normalizedContent.status = resolvedStatus;
      }

      const resolvedSocketErrorMessage =
        payload?.errorMessage ||
        payload?.data?.errorMessage ||
        payload?.content?.errorMessage ||
        payload?.error?.message ||
        payload?.error?.errorMessage ||
        payload?.content?.error?.message ||
        normalizeErrorArray(payload?.errorMessages) ||
        normalizeErrorArray(payload?.errors) ||
        normalizeErrorArray(payload?.content?.errorMessages) ||
        normalizeErrorArray(payload?.content?.errors) ||
        normalizeErrorArray(payload?.data?.errorMessages) ||
        normalizeErrorArray(payload?.data?.errors);

      const messageWithId = {
        ...payload,
        ...(normalizedContent ? { content: normalizedContent } : {}),
        ...(resolvedStatus !== null && resolvedStatus !== undefined
          ? {
              status: resolvedStatus,
              messageStatus:
                payload?.messageStatus !== undefined && payload?.messageStatus !== null
                  ? payload.messageStatus
                  : resolvedStatus,
              chatMessageStatus:
                payload?.chatMessageStatus !== undefined && payload?.chatMessageStatus !== null
                  ? payload.chatMessageStatus
                  : resolvedStatus,
            }
          : {}),
        ...(resolvedSocketErrorMessage ? { errorMessage: resolvedSocketErrorMessage } : {}),
        id: normalizedId,
        // Ensure created exists for proper ordering
        created: payload.created || new Date().toISOString(),
        // Map replyToChatMessageId from socket payload
        replyToChatMessageId: payload.replyToChatMessageId || null,
      };

      if (!messageWithId.messageId) {
        messageWithId.messageId =
          payload.messageId || payload.id || payload.channelMessageIdentifier || normalizedId;
      }

      const incomingKey = getMessageUniqueKey(messageWithId);
      if (incomingKey) {
        const existingIndex = state.messages.findIndex(
          (msg) => getMessageUniqueKey(msg) === incomingKey,
        );
        if (existingIndex !== -1) {
          const newMessages = [...state.messages];
          const existingMessage = newMessages[existingIndex] || {};

          const mergedContent =
            existingMessage.content || messageWithId.content
              ? {
                  ...(existingMessage.content || {}),
                  ...(messageWithId.content || {}),
                }
              : undefined;

          // Preserve socketReceivedAt: if either the existing or incoming
          // message already had a socket-arrival timestamp, keep the EARLIEST
          // one (which represents when the message first surfaced to the UI).
          // This guarantees re-merges from API/fetch don't reset the order.
          const existingArrival =
            typeof existingMessage.socketReceivedAt === 'number'
              ? existingMessage.socketReceivedAt
              : null;
          const incomingArrival =
            typeof messageWithId.socketReceivedAt === 'number'
              ? messageWithId.socketReceivedAt
              : null;
          const preservedArrival =
            existingArrival != null && incomingArrival != null
              ? Math.min(existingArrival, incomingArrival)
              : existingArrival || incomingArrival;

          const mergedMessage = {
            ...existingMessage,
            ...messageWithId,
            ...(mergedContent ? { content: mergedContent } : {}),
            ...(preservedArrival != null ? { socketReceivedAt: preservedArrival } : {}),
            isOptimistic: false,
          };

          newMessages[existingIndex] = mergedMessage;
          const needsFix1 =
            currentThreadId &&
            (!state.messagesThreadId ||
              String(state.messagesThreadId).toLowerCase() !==
                String(currentThreadId).toLowerCase());
          return {
            ...state,
            messages: newMessages,
            ...(needsFix1 && { messagesThreadId: currentThreadId }),
          };
        }
      }

      // If already loaded, check if there's an optimistic message to replace
      // Prefer matching by id/messageId, fallback to same userId
      if (
        messageWithId.messageType === 'user' ||
        messageWithId.messageType === 'user-message' ||
        messageWithId.messageType === 2 ||
        messageWithId.messageType === CoreEnums?.ChatMessageType?.UserMessage ||
        messageWithId.userId
      ) {
        const optimisticIndexById = state.messages.findIndex(
          (msg) =>
            msg.isOptimistic &&
            (msg.id === messageWithId.id ||
              msg.id === messageWithId.messageId ||
              msg.messageId === messageWithId.id),
        );

        let optimisticIndex = optimisticIndexById;
        if (optimisticIndex === -1) {
          // Fallback: match by userId only when there is exactly ONE optimistic
          // message from this user, to avoid replacing the wrong one
          const optimisticsByUser = state.messages.reduce((acc, msg, idx) => {
            if (msg.isOptimistic && msg.userId === messageWithId.userId) {
              acc.push(idx);
            }
            return acc;
          }, []);
          if (optimisticsByUser.length === 1) {
            optimisticIndex = optimisticsByUser[0];
          }
        }

        if (optimisticIndex !== -1) {
          const newMessages = [...state.messages];
          newMessages[optimisticIndex] = { ...messageWithId, isOptimistic: false };
          const needsFix2 =
            currentThreadId &&
            (!state.messagesThreadId ||
              String(state.messagesThreadId).toLowerCase() !==
                String(currentThreadId).toLowerCase());
          return {
            ...state,
            messages: newMessages,
            ...(needsFix2 && { messagesThreadId: currentThreadId }),
          };
        }
      }

      // If no optimistic message to replace, insert in sorted position
      // (desc by ORDER timestamp). Socket-delivered messages get tagged
      // with `socketReceivedAt = Date.now()` so the backend cannot emit a
      // response with a stale `created` and have it land above the
      // ai-thinking events of the same turn (see `getOrderTs` doc).
      const nowMs = Date.now();
      const enrichedMsg = {
        ...messageWithId,
        ...(typeof messageWithId.createdMs !== 'number'
          ? { createdMs: isoToMs(messageWithId.created) }
          : {}),
        socketReceivedAt:
          typeof messageWithId.socketReceivedAt === 'number'
            ? messageWithId.socketReceivedAt
            : nowMs,
      };
      const msgTs = getOrderTs(enrichedMsg);
      let insertAt = 0;
      while (insertAt < state.messages.length && getOrderTs(state.messages[insertAt]) > msgTs) {
        insertAt++;
      }
      const ordered = [...state.messages];
      ordered.splice(insertAt, 0, enrichedMsg);

      // Always ensure messagesThreadId is set when adding a message.
      // This guarantees filteredMessages won't return [] due to a stale/null
      // messagesThreadId — which was the root cause of intermittent message
      // disappearance after AI thinking.
      const needsThreadIdFix =
        currentThreadId &&
        (!state.messagesThreadId ||
          String(state.messagesThreadId).toLowerCase() !== String(currentThreadId).toLowerCase());

      return {
        ...state,
        messages: ordered,
        ...(needsThreadIdFix && { messagesThreadId: currentThreadId }),
      };
    },
    // New reducer to update specific thread state
    updateThreadStatus(state, { payload }) {
      const { threadId, status, updated } = payload;

      // Update the thread in the list of threads if it exists
      const updatedThreads = state.threads.map((thread) => {
        if (thread.id.toString() === threadId.toString()) {
          return {
            ...thread,
            status: status,
            updated: updated || thread.updated,
          };
        }
        return thread;
      });

      // Also update the current chatThread if it is the same thread
      let updatedChatThread = state.chatThread;
      if (state.chatThread?.id?.toString() === threadId.toString()) {
        updatedChatThread = {
          ...state.chatThread,
          status: status,
          updated: updated || state.chatThread.updated,
        };
      }

      return {
        ...state,
        threads: updatedThreads,
        chatThread: updatedChatThread,
      };
    },
    updateThreadControlMode(state, { payload }) {
      const { threadId } = payload || {};
      if (!threadId) {
        return state;
      }

      const controlValue =
        payload?.controlMode !== undefined && payload?.controlMode !== null
          ? payload.controlMode
          : ChatControlMode?.Auto || 1;

      if (!threadId) {
        return state;
      }

      const stringId = threadId.toString();

      const updatedThreads = state.threads.map((thread) => {
        if (thread.id?.toString() === stringId) {
          return {
            ...thread,
            controlMode: controlValue,
          };
        }
        return thread;
      });

      let updatedChatThread = state.chatThread;
      if (state.chatThread?.id?.toString() === stringId) {
        updatedChatThread = {
          ...state.chatThread,
          controlMode: controlValue,
        };
      }

      return {
        ...state,
        threads: updatedThreads,
        chatThread: updatedChatThread,
      };
    },
    updateThreadHandlerType(state, { payload }) {
      const { threadId } = payload || {};
      if (!threadId) {
        return state;
      }

      const handlerValue =
        payload?.handlerType !== undefined && payload?.handlerType !== null
          ? payload.handlerType
          : ChatHandlerType?.AI || 2;

      const stringId = threadId.toString();

      const updatedThreads = state.threads.map((thread) => {
        if (thread.id?.toString() === stringId) {
          return {
            ...thread,
            handlerType: handlerValue,
          };
        }
        return thread;
      });

      let updatedChatThread = state.chatThread;
      if (state.chatThread?.id?.toString() === stringId) {
        updatedChatThread = {
          ...state.chatThread,
          handlerType: handlerValue,
        };
      }

      return {
        ...state,
        threads: updatedThreads,
        chatThread: updatedChatThread,
      };
    },
    // New reducer to add individual thread to store
    addThreadToStore(state, { payload }) {
      // Verify that thread doesn't already exist in the list
      const exists = state.threads.find((t) => t.id.toString() === payload.id.toString());
      if (exists) {
        return state; // Ya existe, no duplicar
      }

      const normalizedControlMode =
        payload.controlMode !== undefined && payload.controlMode !== null
          ? payload.controlMode
          : ChatControlMode?.Auto || 1;

      // Add to beginning of list
      return {
        ...state,
        threads: [{ ...payload, controlMode: normalizedControlMode }, ...state.threads],
        totalCount: state.totalCount + 1,
      };
    },
    // New reducer to remove individual thread from store
    removeThreadFromStore(state, { payload }) {
      const { threadId } = payload;

      // Filter the thread to remove it from the list
      const filteredThreads = state.threads.filter(
        (thread) => thread.id.toString() !== threadId.toString(),
      );

      return {
        ...state,
        threads: filteredThreads,
        totalCount: Math.max(0, state.totalCount - 1),
      };
    },
    // Multi-agent reducers
    saveAgents(state, { payload }) {
      const agentsArray = payload.items || payload || [];
      const count = payload.totalCount ?? payload['@odata.count'] ?? payload.count ?? undefined;
      const pageSize = payload.pageSize || state.agentsPageSize;
      const rawReceived = Array.isArray(payload.items) ? payload.items.length : agentsArray.length;
      let defaultAgent = null;

      if (agentsArray.length > 0) {
        defaultAgent =
          agentsArray.find((agent) => agent.id === 'eb77a9b1-c65d-48dd-a76c-61257cc65505') ||
          agentsArray[0];
      }

      const totalAgents = count != null ? count : agentsArray.length;
      // Exactly a full page hints there may be more; a short page (or a
      // non-paginated response returning more than a page) means everything is
      // already loaded. When the count is known, also stop once we have enough.
      const fullPage = rawReceived === pageSize;
      const hasMoreAgents = count != null ? agentsArray.length < count && fullPage : fullPage;

      return {
        ...state,
        agents: agentsArray,
        selectedAgent: state.selectedAgent || defaultAgent,
        agentsPage: payload.page || 1,
        totalAgents,
        hasMoreAgents,
      };
    },
    appendAgents(state, { payload }) {
      const newAgents = payload.items || payload || [];
      const existingAgents = state.agents || [];
      const count = payload.totalCount ?? payload['@odata.count'] ?? payload.count ?? undefined;
      const pageSize = payload.pageSize || state.agentsPageSize;
      // Raw count from the server (before dedup) is what tells us whether this
      // was a full page. Dedup can hide overlaps with prepended agents, so we
      // must NOT use the filtered length for end-detection.
      const rawReceived = Array.isArray(payload.items) ? payload.items.length : newAgents.length;

      const filteredNewAgents = newAgents.filter(
        (newAgent) => !existingAgents.some((existing) => existing.id === newAgent.id),
      );

      const merged = [...existingAgents, ...filteredNewAgents];
      const totalAgents = count != null ? count : Math.max(state.totalAgents, merged.length);

      // End reached when the server returns a short/empty page. This is immune
      // to `$count` exceeding the number of agents pagination actually yields.
      let hasMoreAgents;
      if (rawReceived < pageSize) {
        hasMoreAgents = false;
      } else if (count != null) {
        hasMoreAgents = merged.length < count;
      } else {
        hasMoreAgents = true;
      }

      return {
        ...state,
        agents: merged,
        agentsPage: payload.page || state.agentsPage,
        totalAgents,
        hasMoreAgents,
      };
    },
    setAgentSearchTerm(state, { payload }) {
      return { ...state, agentSearchTerm: payload || '' };
    },
    setLoadingAgentSearch(state, { payload }) {
      return { ...state, loadingAgentSearch: payload };
    },
    setLoadingMoreAgentSearch(state, { payload }) {
      return { ...state, loadingMoreAgentSearch: payload };
    },
    clearAgentSearch(state) {
      return {
        ...state,
        agentSearchTerm: '',
        agentSearchResults: [],
        agentSearchPage: 1,
        totalAgentSearch: 0,
        hasMoreAgentSearch: false,
        loadingAgentSearch: false,
        loadingMoreAgentSearch: false,
      };
    },
    saveAgentSearch(state, { payload }) {
      const results = payload.items || payload || [];
      const count = payload.totalCount ?? payload['@odata.count'] ?? payload.count ?? undefined;
      const pageSize = payload.pageSize || state.agentsPageSize;
      const rawReceived = Array.isArray(payload.items) ? payload.items.length : results.length;
      const fullPage = rawReceived === pageSize;
      return {
        ...state,
        agentSearchResults: results,
        agentSearchPage: payload.page || 1,
        totalAgentSearch: count != null ? count : results.length,
        hasMoreAgentSearch: count != null ? results.length < count && fullPage : fullPage,
        loadingAgentSearch: false,
      };
    },
    appendAgentSearch(state, { payload }) {
      const newResults = payload.items || payload || [];
      const existing = state.agentSearchResults || [];
      const count = payload.totalCount ?? payload['@odata.count'] ?? payload.count ?? undefined;
      const pageSize = payload.pageSize || state.agentsPageSize;
      const rawReceived = Array.isArray(payload.items) ? payload.items.length : newResults.length;

      const filtered = newResults.filter((a) => !existing.some((e) => e.id === a.id));
      const merged = [...existing, ...filtered];

      let hasMoreAgentSearch;
      if (rawReceived < pageSize) {
        hasMoreAgentSearch = false;
      } else if (count != null) {
        hasMoreAgentSearch = merged.length < count;
      } else {
        hasMoreAgentSearch = true;
      }

      return {
        ...state,
        agentSearchResults: merged,
        agentSearchPage: payload.page || state.agentSearchPage,
        totalAgentSearch: count != null ? count : Math.max(state.totalAgentSearch, merged.length),
        hasMoreAgentSearch,
        loadingMoreAgentSearch: false,
      };
    },
    setSelectedAgent(state, { payload }) {
      return {
        ...state,
        selectedAgent: payload,
      };
    },
    updateAgentsList(state, { payload }) {
      return {
        ...state,
        agents: payload.agents,
        totalAgents: payload.totalAgents,
      };
    },
    setLoadingAgents(state, { payload }) {
      return {
        ...state,
        loadingAgents: payload,
      };
    },
    setLoadingMoreAgents(state, { payload }) {
      return {
        ...state,
        loadingMoreAgents: payload,
      };
    },
    setHasAllowedAgents(state, { payload }) {
      return {
        ...state,
        hasAllowedAgents: payload,
      };
    },
    setIsOpen(state, { payload }) {
      return {
        ...state,
        isOpen: payload,
      };
    },
    setOpenSpecificChatTs(state, { payload }) {
      return {
        ...state,
        openSpecificChatTs: payload,
      };
    },
    resetState() {
      return initialState;
    },
    saveThreadParticipants(state, { payload }) {
      const { chatThreadId, participants, totalCount } = payload;
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: participants,
        },
        participantsTotalCount: {
          ...state.participantsTotalCount,
          [chatThreadId]: totalCount,
        },
      };
    },
    optimisticUpdateParticipantAccess(state, { payload }) {
      const { chatThreadId, participantId, newAccessMode } = payload;
      const list = state.threadParticipants[chatThreadId] || [];
      const updatedList = list.map((p) =>
        p.id === participantId ? { ...p, accessMode: newAccessMode } : p,
      );
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: updatedList,
        },
      };
    },
    optimisticAddParticipants(state, { payload }) {
      const { chatThreadId, users, accessMode } = payload;
      const current = state.threadParticipants[chatThreadId] || [];
      const toSafeText = (value, fallbackPrefix = 'Usuario') => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return String(value);
        if (value && typeof value === 'object') {
          const candidates = [
            value.fullName,
            value.userName,
            value.name,
            value.email,
            value.title,
            value.value,
            value.label,
          ];
          const found = candidates.find((item) => typeof item === 'string' && item.trim());
          if (found) return found;
          if (value.id) return `${fallbackPrefix} ${value.id}`;
        }
        return `${fallbackPrefix}`.trim();
      };

      const newItems = (users || []).map((u) => {
        const userId = typeof u === 'object' ? u.id : u;
        const fullName =
          typeof u === 'object'
            ? toSafeText(
                u.fullName || u.userName || u.name || u.email || u.label || u.value,
                'Usuario',
              )
            : toSafeText(u, 'Usuario');
        const userName =
          typeof u === 'object'
            ? toSafeText(
                u.userName || u.fullName || u.name || u.email || u.label || u.value,
                'Usuario',
              )
            : toSafeText(u, 'Usuario');
        return {
          id: `temp-${userId}-${Date.now()}`,
          user: { id: userId, fullName, userName },
          userName: userName || fullName,
          accessMode,
          isOptimistic: true,
        };
      });
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: [...newItems, ...current],
        },
        participantsTotalCount: {
          ...state.participantsTotalCount,
          [chatThreadId]:
            (state.participantsTotalCount[chatThreadId] || current.length) + newItems.length,
        },
      };
    },
    optimisticRemoveParticipant(state, { payload }) {
      const { chatThreadId, participantId } = payload;
      const list = state.threadParticipants[chatThreadId] || [];
      const updatedList = list.filter((p) => p.id !== participantId);
      return {
        ...state,
        threadParticipants: {
          ...state.threadParticipants,
          [chatThreadId]: updatedList,
        },
        participantsTotalCount: {
          ...state.participantsTotalCount,
          [chatThreadId]: Math.max(
            (state.participantsTotalCount[chatThreadId] || list.length) - 1,
            0,
          ),
        },
      };
    },
    setLoadingParticipants(state, { payload }) {
      return {
        ...state,
        loadingParticipants: payload,
      };
    },
  },
};

export default ChatModel;
